<script lang="ts" setup>
  import type { ViewSource } from '~/types/route'
  import OtpFormContent from './OtpFormContent.vue'
  const props = defineProps<{
    modalId: string
    source: ViewSource
    title: string
    message: string
    showPasswordInput?: boolean
    transparentModalStyle?: boolean
    onSubmit: (code: string, password?: string) => Promise<void>
    onSuccessfulVerifyCode: () => Promise<void>
    onSignOut: () => void
    onDismiss: () => void
  }>()

  const otpFormRef = ref<typeof OtpFormContent | undefined>()
  const contentEl = useKeyboardOpenAndScroll()
  const { alertContent, isAlertVisible } = useGenericAlert()
  const isFormValid = ref(false)
  const isSubmitting = ref(false)

  const onDismissModal = () => {
    modalController.dismiss(undefined, undefined, props.modalId)
    props.onDismiss()
  }
</script>

<template>
  <div v-if="props.transparentModalStyle" class="modal-content">
    <div class="content-container">
      <div class="header-container">
        <div class="spacer" />
        <h1 class="page-title">{{ title }}</h1>

        <GCloseButton @click="onDismissModal" />
      </div>
      <OtpFormContent
        ref="otpFormRef"
        :show-password-input="props.showPasswordInput"
        :message="props.message"
        :on-submit="props.onSubmit"
        :on-successful-verify-code="props.onSuccessfulVerifyCode"
        :on-sign-out="props.onSignOut"
        @is-form-valid="isFormValid = $event"
        @is-submitting="isSubmitting = $event"
      />
      <div>
        <GButton
          :disabled="!isFormValid"
          label="Submit"
          :should-show-loading-animation="isSubmitting"
          animation-color="light"
          block
          @click.prevent.stop="otpFormRef?.onSubmit()"
        />
      </div>
    </div>
  </div>
  <template v-else>
    <GPageHeader compact disable-box-shadow :hide-back-button="true" :title="props.title">
      <template #end>
        <GCloseButton @click="onDismissModal" />
      </template>
    </GPageHeader>

    <ion-content ref="contentEl" class="ion-padding-horizontal">
      <div class="content-container">
        <OtpFormContent
          ref="otpFormRef"
          :show-password-input="props.showPasswordInput"
          :message="props.message"
          :on-submit="props.onSubmit"
          :on-successful-verify-code="props.onSuccessfulVerifyCode"
          :on-sign-out="props.onSignOut"
          @is-form-valid="isFormValid = $event"
          @is-submitting="isSubmitting = $event"
        />
      </div>
    </ion-content>

    <ion-footer>
      <ion-toolbar>
        <GButton
          :disabled="!isFormValid"
          label="Submit"
          :should-show-loading-animation="isSubmitting"
          animation-color="light"
          block
          @click.prevent.stop="otpFormRef?.onSubmit()"
        />
      </ion-toolbar>
    </ion-footer>
  </template>

  <GAlert
    :is-open="isAlertVisible"
    :header="alertContent.header"
    :message="alertContent.message"
    @will-dismiss="isAlertVisible = false"
  />
</template>

<style lang="scss" scoped>
  .header-container {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    margin-bottom: 16px;

    .page-title {
      @include typography-header-4;

      margin: 0;
      position: absolute;
      left: 0;
      right: 0;
      text-align: center;
      pointer-events: none; // allows button clicks through title
    }
  }

  .modal-content {
    height: 100vh;
    display: flex; /* Enable flexbox */
    flex-direction: column;
    justify-content: center; /* Center horizontally */
    align-items: center; /* Center vertically */

    .content-container {
      background-color: white;
      padding: 16px;
      border-radius: 8px;
      max-width: 300px;

      display: flex;
      flex-direction: column;
      align-items: stretch;
      gap: 4px;
    }
  }
</style>
